import { FC, useState } from "react";
import {
  Box,
  Flex,
  PrimaryButton,
  SecondaryButton,
  ToggleGroup,
  Text,
  LinkText,
} from "flicket-ui";
import CustomModal from "~components/CustomModal";
import styled from "styled-components";
import { CookiePreferences } from "~context/cookieConsentContext";

interface CookiePreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  preferences: CookiePreferences;
  onSave: (preferences: CookiePreferences) => void;
}

const CookieSectionsContainer = styled(Box)`
  border-radius: 4px;
  overflow: hidden;
  background-color: #f8f8f8;
  margin-bottom: 16px;
`;

const CookieSectionItem = styled(Box)`
  border-bottom: 1px solid #eaeaea;

  &:last-child {
    border-bottom: none;
  }
`;

export const CookiePreferencesModal: FC<CookiePreferencesModalProps> = ({
  isOpen,
  onClose,
  preferences,
  onSave,
}) => {
  const [localPreferences, setLocalPreferences] = useState<CookiePreferences>({
    ...preferences,
  });

  const handleToggleChange = (key: keyof CookiePreferences) => {
    if (key === "essential") return;

    setLocalPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = () => {
    onSave(localPreferences);
  };

  const handleAcceptAll = () => {
    const allEnabled = {
      essential: true,
      marketing: true,
      analytics: true,
    };
    setLocalPreferences(allEnabled);
    onSave(allEnabled);
  };

  return (
    <CustomModal
      isOpen={isOpen}
      close={onClose}
      bg="white"
      zIndex={2000}
      clickOutsideToClose={true}
    >
      <CustomModal.Header>Your Privacy</CustomModal.Header>
      <CustomModal.Content bg="white">
        <Text variant="regular" mb={4}>
          This site uses tracking technologies. You may opt in or opt out of the
          use of these technologies.
        </Text>

        <CookieSectionsContainer>
          <CookieSectionItem>
            <Flex p={3}>
              <ToggleGroup
                type="regular"
                value={localPreferences.marketing}
                onChange={() => handleToggleChange("marketing")}
                title="Marketing"
                description="Marketing cookies and services are used to deliver personalized advertisements, promotions, and offers. These technologies enable targeted advertising and marketing campaigns by collecting information about users' interests, preferences, and online activities."
              />
            </Flex>
          </CookieSectionItem>

          <CookieSectionItem>
            <Flex p={3}>
              <ToggleGroup
                type="regular"
                value={localPreferences.analytics}
                onChange={() => handleToggleChange("analytics")}
                title="Analytics"
                description="Analytics cookies and services are used for collecting statistical information about how visitors interact with a website and provide enhanced functionalities. These technologies provide insights into website usage, visitor behavior, and site performance, as well as features like language preferences and customized layouts to enhance your user experience."
              />
            </Flex>
          </CookieSectionItem>

          <CookieSectionItem>
            <Flex p={3}>
              <ToggleGroup
                type="regular"
                value={true}
                /* Essential cookies are always required and cannot be toggled off */
                /* eslint-disable-next-line @typescript-eslint/no-empty-function */
                onChange={() => {}}
                title="Essential"
                description="Essential cookies and services are used to enable core website features, such as ensuring the security of the website."
              />
            </Flex>
          </CookieSectionItem>
        </CookieSectionsContainer>
      </CustomModal.Content>
      <CustomModal.Footer bg="white" py={3}>
        <Box width="100%">
          <Flex
            justifyContent="space-between"
            width="100%"
            flexDirection={["column", "row"]}
            alignItems={["stretch", "center"]}
          >
            <Flex mb={[2, 0]}>
              <SecondaryButton onClick={handleAcceptAll}>
                Accept all
              </SecondaryButton>
            </Flex>
            <Box>
              <PrimaryButton onClick={handleSave} width={["100%", "auto"]}>
                Save
              </PrimaryButton>
            </Box>
          </Flex>

          <Box mt={3}>
            <Text variant="small">
              Read how we handle your data in our{" "}
              <LinkText color="P300" href="/privacy-policy">
                Privacy Policy
              </LinkText>
            </Text>
          </Box>
        </Box>
      </CustomModal.Footer>
    </CustomModal>
  );
};
